import { MAX_TEXT_LENGTH, validateJob } from '../../core/profile';
import type { JobInput } from '../../core/types';
import { jobDates } from '../format';
import { EntrySection, type EntryOps, type Values } from './EntryForm';

type JobView = JobInput & { id: string };

const toBody = (v: Values): JobInput => ({
  role: String(v.role ?? ''),
  schoolOrDistrict: String(v.schoolOrDistrict ?? ''),
  startMonth: String(v.startMonth ?? ''),
  endMonth: v.current ? null : String(v.endMonth ?? ''),
});

/** Job history (R6): role, school or district, and month/year dates, or "Current role". */
export function JobList({ jobs, ops }: { jobs: readonly JobView[]; ops?: EntryOps<JobInput> }) {
  return <EntrySection
    heading="Job history" noun="job" entries={jobs} ops={ops}
    label={(job) => job.role}
    render={(job) => <>
      <h3>{job.role}</h3>
      <p className="entry-detail">{job.schoolOrDistrict}</p>
      <p className="entry-meta">{jobDates(job.startMonth, job.endMonth)}</p>
    </>}
    form={(job) => ({
      fields: [
        { name: 'role', label: 'Role', kind: 'text', max: MAX_TEXT_LENGTH },
        { name: 'schoolOrDistrict', label: 'School or district', kind: 'text', max: MAX_TEXT_LENGTH },
        { name: 'startMonth', label: 'Start date', kind: 'month' },
        { name: 'current', label: 'Current role', kind: 'checkbox' },
        { name: 'endMonth', label: 'End date', kind: 'month', show: (v) => !v.current },
      ],
      initial: job
        ? { role: job.role, schoolOrDistrict: job.schoolOrDistrict, startMonth: job.startMonth, endMonth: job.endMonth ?? '', current: job.endMonth === null }
        : { role: '', schoolOrDistrict: '', startMonth: '', endMonth: '', current: false },
      validate: (v) => {
        const body = toBody(v);
        // The core reads a blank end as the current role; here it means the owner has not filled it in.
        if (!v.current && !body.endMonth) {
          const rest = validateJob({ ...body, endMonth: null });
          return rest.ok ? { field: 'endMonth', reason: 'required' } : rest;
        }
        const checked = validateJob(body);
        return checked.ok ? null : checked;
      },
      toBody,
    })}
  />;
}
